"use client";

import React from "react";
import { Shield, Phone, Wrench, ShieldCheck, Loader, Heart, Shirt } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
const inputClasses = "flex h-12 w-full rounded-md border border-border bg-background px-4 py-2 text-base text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50 transition-colors border-b-2 border-t-0 border-x-0 rounded-none";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="py-16 text-center space-y-5 animate-in fade-in duration-500">
                <div className="inline-flex items-center justify-center p-4 bg-primary/10 rounded-lg mb-2">
                    <Heart className="size-12 text-primary" />
                </div>
                <h3 className="text-3xl font-default font-bold text-foreground">With Love, Thank You</h3>
                <p className="text-muted-foreground text-lg max-w-md mx-auto">{successMessage}</p>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground font-medium">Full Name</FormLabel>
                            <FormControl>
                                <input {...field} className={inputClasses} placeholder="Your Name" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
                <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-foreground font-medium">Phone Number</FormLabel>
                            <FormControl>
                                <input {...field} type="tel" className={inputClasses} placeholder="(555) 000-0000" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <FormField control={control} name="service" rules={{
      required: "Please select a status"
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-foreground font-medium">Attendance Status</FormLabel>
                        <FormControl>
                            <select {...field} className={inputClasses}>
                                <option value="" disabled>Will you join us?</option>
                                <option value="attending">Joyfully Accepts</option>
                                <option value="declining">Regretfully Declines</option>
                                <option value="maybe">Still Deciding</option>
                            </select>
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-foreground font-medium">Dietary Requirements & Song Requests</FormLabel>
                        <FormControl>
                            <textarea {...field} className={`${inputClasses} min-h-[120px] resize-y py-3`} placeholder="Please let us know any allergies or a song that will get you on the dance floor..." />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            {submitError && <div className="p-4 bg-destructive/20 border border-destructive rounded-md">
                    <p className="text-foreground text-sm font-medium">{submitError}</p>
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-default font-bold mt-4 shadow-md hover:shadow-lg transition-all bg-primary text-primary-foreground rounded-md">
                {isSubmitting ? <>
                        <Loader className="animate-spin mr-2 size-6" />
                        Sending Response...
                    </> : 'Send RSVP'}
            </Button>
        </Form>;
}
export default function ContactForm() {
  return <section data-section-id="258484" id="kindly-respond" className="bg-background py-16 md:py-24 relative text-foreground">
            <div className="container mx-auto px-4">
                <div className="max-w-6xl mx-auto">
                    <Card className="flex flex-col gap-6 bg-card text-card-foreground border border-primary/20 shadow-xl overflow-hidden rounded-lg !p-0">
                        <div className="grid grid-cols-1 lg:grid-cols-5">
                            {}
                            <div className="bg-secondary p-8 lg:p-12 lg:col-span-2 flex flex-col justify-between relative overflow-hidden">
                                {}
                                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjIiIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSIvPjwvc3ZnPg==')] opacity-10 pointer-events-none"></div>
                                
                                <div className="relative z-10">
                                    <Heart className="size-14 text-primary mb-8 opacity-50" />
                                    <h3 className="text-3xl font-default font-bold text-foreground mb-4 leading-tight">
                                        The Celebration of Love
                                    </h3>
                                    <p className="text-muted-foreground text-lg mb-10 leading-relaxed">
                                        We are so excited to share this special day with our closest family and friends.
                                    </p>
                                    <ul className="space-y-6">
                                        <li className="flex items-center gap-4 text-foreground font-medium text-lg">
                                            <div className="bg-primary/10 p-2.5 rounded-md shrink-0">
                                                <Shirt className="size-6 text-primary" />
                                            </div>
                                            <span>Formal Attire Requested</span>
                                        </li>
                                        <li className="flex items-center gap-4 text-foreground font-medium text-lg">
                                            <div className="bg-primary/10 p-2.5 rounded-md shrink-0">
                                                <ShieldCheck className="size-6 text-primary" />
                                            </div>
                                            <span>Adults Only Reception</span>
                                        </li>
                                    </ul>
                                </div>
                                <div className="mt-12 pt-8 border-t border-border relative z-10">
                                    <p className="text-sm text-muted-foreground mb-4 font-bold uppercase tracking-widest">Questions for the couple?</p>
                                    <Button variant="outline" className="w-full font-default font-bold text-lg h-14 shadow-sm border-primary/30 hover:bg-primary/5" asChild>
                                        <Link to="/TheBigDayPage">
                                            <Phone className="mr-2 size-5" />
                                            Event Details
                                        </Link>
                                    </Button>
                                </div>
                            </div>

                            {}
                            <div className="p-8 lg:p-12 lg:col-span-3 bg-background">
                                <div className="mb-10">
                                    <h2 className="text-3xl md:text-4xl font-default font-bold text-foreground mb-4">
                                        Kindly Respond
                                    </h2>
                                    <p className="text-muted-foreground text-lg leading-relaxed">
                                        Please let us know if you can make it by the first of September. We can't wait to celebrate with you!
                                    </p>
                                </div>

                                <FormProvider formKey="rsvp_form_rsvp" formId="rsvp_form_rsvp" sectionName="ContactForm" defaultValues={{
                name: "",
                phone: "",
                service: "",
                message: ""
              }} submitText="Send RSVP" successMessage="Thank you for your response! We have received your RSVP and look forward to seeing you.">
                                    <ContactFormInner />
                                </FormProvider>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        </section>;
}